import { Box, ButtonBase, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { hideBelowWidthSx } from '../collapse';
import { Chevron } from '../primitives';
import { VALUE_WIDTH, type LedgerCollapse } from '../types';

export interface BandProps {
  collapse: LedgerCollapse;
  primary: string;
  /** Rendered after a "·" — a gang, a plot, a date range. */
  secondary?: string;
  count?: number;
  /** Generic slot beside the label; the ledger screens put a percentage here. */
  inline?: ReactNode;
  /** Hide the inline slot at or below this width. Always shown when omitted. */
  inlineHiddenBelow?: number;
  /** Sits in a VALUE_WIDTH box so band totals land on the Value column. */
  trailing?: ReactNode;
  open: boolean;
  onToggle: () => void;
  /**
   * Primary action on the label, beside the toggle: opening the thing the
   * band groups by (a plot, a site, a labour item). Without it the label is a
   * second way to toggle, which is what a band with nothing to open wants.
   */
  onLabelClick?: () => void;
  showValues?: boolean;
  'data-testid'?: string;
}

const labelButtonSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
  borderRadius: 0.5,
  px: 0.5,
  mx: -0.5,
  '&:hover .band-label': { color: 'primary.main' },
} as const;

/**
 * A collapsible group heading. Knows about labels, a count, one generic inline
 * slot and one trailing slot — nothing about what is inside the group.
 *
 * The toggle and the label are sibling controls rather than one nested inside
 * the other: a button inside a button is invalid, and it costs both keyboard
 * and screen-reader users the second action.
 */
export const Band = ({
  collapse,
  primary,
  secondary,
  count,
  inline,
  inlineHiddenBelow,
  trailing,
  open,
  onToggle,
  onLabelClick,
  showValues = true,
  'data-testid': dataTestId,
}: BandProps) => {
  const labelToggles = onLabelClick === undefined;

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        pl: 1,
        pr: 2,
        py: 0.75,
        minHeight: 38,
        bgcolor: 'action.hover',
        borderBottom: '1px solid',
        borderColor: 'divider',
        textAlign: 'left',
      }}
    >
      <ButtonBase
        onClick={onToggle}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} ${primary}`}
        sx={{ borderRadius: '50%', p: 0.25, flexShrink: 0 }}
      >
        <Chevron open={open} />
      </ButtonBase>

      <ButtonBase
        onClick={onLabelClick ?? onToggle}
        aria-expanded={labelToggles ? open : undefined}
        sx={labelButtonSx}
      >
        <Typography
          className="band-label"
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{ transition: 'color 150ms ease' }}
        >
          {primary}
        </Typography>
        {secondary && (
          <Typography variant="body2" color="text.secondary" noWrap>
            · {secondary}
          </Typography>
        )}
      </ButtonBase>

      {count !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {count}
        </Typography>
      )}
      {inline && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            ml: 1,
            ...(inlineHiddenBelow
              ? hideBelowWidthSx(collapse.container, inlineHiddenBelow)
              : {}),
          }}
        >
          {inline}
        </Box>
      )}
      <Box sx={{ flex: 1 }} />
      {showValues && (
        <Box sx={{ width: VALUE_WIDTH, flexShrink: 0, textAlign: 'right' }}>
          {trailing}
        </Box>
      )}
    </Box>
  );
};
